import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min';
import axiosInstance from '../../../../../api';

// Modal component for adding a new vital sign
const VitalSignsModal = ({ isOpen, onClose, onSave, isEditable, existingVitalSigns, caseId }) => {
  const [vitalSignName, setVitalSignName] = useState('');
  const [suggestedVitals, setSuggestedVitals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const modalInstanceRef = useRef(null);

  // Initialize Bootstrap modal
  useEffect(() => {
    const element = document.getElementById('vital-signs-modal');
    if (!element) {
      console.warn('Vital Signs Modal element not found');
      return;
    }

    let modalInstance = null;
    try {
      modalInstance = new bootstrap.Modal(element, { backdrop: 'static', keyboard: false });
      modalInstanceRef.current = modalInstance;
    } catch (error) {
      console.warn('Error initializing Vital Signs Modal:', error.message);
      return;
    }

    return () => {
      if (modalInstanceRef.current && element.isConnected) {
        try {
          modalInstanceRef.current.hide();
          modalInstanceRef.current.dispose();
        } catch (error) {
          console.warn('Error during Vital Signs Modal cleanup:', error.message);
        }
        modalInstanceRef.current = null;
      }
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop && backdrop.isConnected) {
        backdrop.remove();
      }
      if (document.body.classList.contains('modal-open')) {
        document.body.classList.remove('modal-open');
      }
    };
  }, []);

  // Show or hide modal based on isOpen prop
  useEffect(() => {
    if (isOpen && modalInstanceRef.current) {
      try {
        modalInstanceRef.current.show();
      } catch (error) {
        console.warn('Error showing Vital Signs Modal:', error.message);
      }
    } else if (modalInstanceRef.current) {
      try {
        modalInstanceRef.current.hide();
      } catch (error) {
        console.warn('Error hiding Vital Signs Modal:', error.message);
      }
    }
  }, [isOpen]);

  // Fetch suggested vital signs from API
  const fetchSuggestedVitals = async () => {
    try {
      setIsLoading(true);
      const authToken = sessionStorage.getItem('auth_token') || '';
      if (!authToken) {
        console.warn('No auth token found');
        setSuggestedVitals([]);
        return;
      }
      const response = await axiosInstance.get('/ems/vital-signs-suggestions', {
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const vitals = response.data?.data || [];
      setSuggestedVitals(vitals);
    } catch (error) {
      console.error('Error fetching vital signs suggestions:', error.response?.data || error.message);
      setSuggestedVitals([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch suggested vital signs on component mount
  useEffect(() => {
    fetchSuggestedVitals();
  }, []);

  // Handle save action for the vital sign
  const handleSave = () => {
    if (!isEditable) {
      alert('You do not have permission to add vital signs.');
      return;
    }
    if (!vitalSignName.trim()) {
      alert('Please enter a valid vital sign name.');
      return;
    }
    const key = vitalSignName.toLowerCase().replace(/\s+/g, '_');
    if (Object.keys(existingVitalSigns).includes(key)) {
      alert('This vital sign already exists.');
      return;
    }
    onSave({ [key]: '' });
    setVitalSignName('');
    onClose();
  };

  // Handle clicking on a suggested vital sign
  const handleSuggestedVitalClick = (vital) => {
    if (!isEditable) {
      alert('You do not have permission to add vital signs.');
      return;
    }
    const key = vital.toLowerCase().replace(/\s+/g, '_');
    if (Object.keys(existingVitalSigns).includes(key)) {
      alert('This vital sign already exists.');
      return;
    }
    onSave({ [key]: '' });
    setVitalSignName('');
    onClose();
  };

  // Render the modal
  return (
    <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} id="vital-signs-modal" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Vital Sign</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setVitalSignName('');
                onClose();
              }}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Vital Sign Name</label>
              <input
                type="text"
                className="form-control"
                value={vitalSignName}
                onChange={(e) => setVitalSignName(e.target.value)}
                placeholder="Enter vital sign name"
                disabled={!isEditable}
              />
            </div>
            {isLoading ? (
              <p>Loading suggestions...</p>
            ) : (
              suggestedVitals.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Suggested Vital Signs</label>
                  <div>
                    {suggestedVitals.map((vital, index) => (
                      <button
                        key={index}
                        type="button"
                        className="btn btn-outline-primary btn-sm me-2 mb-2"
                        onClick={() => handleSuggestedVitalClick(vital)}
                        disabled={!isEditable || Object.keys(existingVitalSigns).includes(vital.toLowerCase().replace(/\s+/g, '_'))}
                      >
                        {vital}
                      </button>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSave}
              disabled={!isEditable}
            >
              Save
            </button>
            <button
              type="button"
              className="btn bg-danger-subtle text-danger font-medium"
              onClick={() => {
                setVitalSignName('');
                onClose();
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// PropTypes for type checking
VitalSignsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  existingVitalSigns: PropTypes.object.isRequired,
  caseId: PropTypes.string.isRequired,
};

export default VitalSignsModal;